// Baixa de Pagamento (Suporte > Baixa de Pagamento, c056): busca pelo que o usuário tem na mão, uma ficha por vez
// (parcelas dos acordos, débitos avulsos e histórico) e o painel fixo com os dados do pagamento. Estorno com a c086.
import React, { useMemo, useState } from "react";
import {
    Alert, Card, Chip, FormControlLabel, Grid, Icon, InputLabel, LinearProgress, Switch, Tab, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tabs,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { get, post, postUpload } from "utils/WebService";
import { infoAviso } from "utils/Functions";
import { avisarFichaAlterada } from "utils/fichaAlterada";
import { toCurrency } from "utils/NumberUtils";
import { useMaterialUIController } from "context";
import { CLARO, coresDoTema, FONTE, TEXTO_COLORIDO_ESCURO } from "assets/coresTema";
import TabelaParcelas from "./TabelaParcelas";
import TabelaDebitos from "./TabelaDebitos";
import HistoricoPagamentos from "./HistoricoPagamentos";
import PainelPagamento from "./PainelPagamento";
import { arredondar, dataBR, diferenca, escaparHtml, numero } from "./baixaUtils";

const valorEditavel = (v) => (v || 0).toFixed(2).replace(".", ",");

// numero = cvId da ficha (tbDevedorCarteira), o código da ficha no sistema
const rotuloFicha = (f) => `Ficha #${f.numero}${f.carteira ? ` · ${f.carteira}` : ""}${f.honorario ? " · HO" : ""}`;

const soDigitos = (t) => String(t || "").replace(/\D/g, "").replace(/^0+/, "");

// Linha abaixo do nome: CPF/CNPJ, o código do cliente só quando não repete o documento (cliente cuja chave é o CNPJ,
// ex.: TAURUS) e, com a ficha aberta, o código dela (cvId da tbDevedorCarteira)
const identificacao = (documento, codigoCliente, codigoFicha) => {
    const repete = codigoCliente && soDigitos(codigoCliente) !== "" && soDigitos(codigoCliente) === soDigitos(documento);
    return [
        documento || "sem CPF/CNPJ",
        codigoCliente && !repete ? `cód. cliente ${codigoCliente}` : null,
        codigoFicha ? `código ${codigoFicha}` : null,
    ].filter(Boolean).join(" · ");
};

export default function BaixaPagamentoConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);

    const [busca, setBusca] = useState("");
    const [buscando, setBuscando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [ficha, setFicha] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [aba, setAba] = useState("parcelas");
    const [mostrarPagas, setMostrarPagas] = useState(false);
    const [selecao, setSelecao] = useState({});
    const [enviando, setEnviando] = useState(false);
    const [painelKey, setPainelKey] = useState(0);
    const [aviso, setAviso] = useState(null);

    const abrirFicha = (idFicha, avisoDepois) => {
        // Recarga da mesma ficha (depois de baixar ou estornar) fica na aba em que a pessoa estava
        const mesmaFicha = Boolean(ficha && ficha.ficha && ficha.ficha.id === idFicha);
        setCarregando(true);
        get(`/baixa-pagamento/ficha/${encodeURIComponent(idFicha)}`)
            .then(dados => {
                setFicha(dados);
                setSelecao({});
                setPainelKey(k => k + 1);
                const temParcela = (dados.acordos || []).some(a => a.emAberto > 0);
                if (!mesmaFicha) setAba(temParcela || (dados.debitos || []).length === 0 ? "parcelas" : "debitos");
                setAviso(avisoDepois || null);
            })
            .catch(err => setAviso({ tipo: "error", texto: err.message || "Erro ao carregar a ficha." }))
            .finally(() => setCarregando(false));
    };

    // Um devedor só: abre a ficha sugerida (achada por ficha, acordo, boleto ou a aberta na Base de Dados), a única ou a
    // única com algo em aberto; senão mostra a lista para escolher
    const mostrarResultado = (res) => {
        const lista = res?.resultados || [];
        setResultado(res);
        setFicha(null);
        setSelecao({});
        if (lista.length === 0) {
            setAviso({ tipo: "info", texto: "Nenhum devedor encontrado (entre as carteiras e os clientes que você vê)." });
        } else if (lista.length === 1) {
            const r = lista[0];
            const unica = r.fichas.length === 1 ? r.fichas[0].id : null;
            const comAberto = r.fichas.filter(f => f.parcelasEmAberto > 0 || f.debitosAvulsos > 0);
            const escolhida = r.idFichaSugerida || unica || (comAberto.length === 1 ? comAberto[0].id : null);
            if (escolhida) abrirFicha(escolhida);
        }
    };

    const pesquisar = () => {
        const termo = busca.trim();
        if (termo.length < 2) {
            setAviso({ tipo: "info", texto: "Digite ao menos 2 caracteres para buscar." });
            return;
        }
        setBuscando(true);
        setAviso(null);
        get(`/baixa-pagamento/buscar?q=${encodeURIComponent(termo)}`)
            .then(mostrarResultado)
            .catch(err => setAviso({ tipo: "error", texto: err.message || "Erro na busca." }))
            .finally(() => setBuscando(false));
    };

    // Dois cliques no campo: a ficha aberta na Base de Dados (ou na Fila de Trabalho), guardada por ela na sessão
    const trazerFichaAberta = () => {
        const idDevedor = sessionStorage.getItem("activeDevedorId");
        if (!idDevedor) {
            setAviso({ tipo: "info", texto: "Nenhuma ficha aberta na Base de Dados. Abra a ficha lá e dê dois cliques no campo de busca para trazê-la." });
            return;
        }
        const idFicha = sessionStorage.getItem("activeFichaId");
        setBusca(sessionStorage.getItem("activeDevedorNome") || "");
        setBuscando(true);
        setAviso(null);
        get(`/baixa-pagamento/devedor/${encodeURIComponent(idDevedor)}${idFicha ? `?ficha=${encodeURIComponent(idFicha)}` : ""}`)
            .then(mostrarResultado)
            .catch(err => setAviso({ tipo: "error", texto: err.message || "Erro ao trazer a ficha aberta." }))
            .finally(() => setBuscando(false));
    };

    const novaBusca = () => {
        setBusca("");
        setResultado(null);
        setFicha(null);
        setSelecao({});
        setAviso(null);
    };

    // ------------------------------------------------------------------------------------------- seleção dos itens
    const itensSelecionados = Object.values(selecao);
    const selecaoContrato = itensSelecionados.length > 0 && itensSelecionados[0].contrato;

    const incluir = (atual, item) => {
        // Parcelas e débitos avulsos não vão juntos na mesma baixa: o de outro tipo substitui a seleção
        const base = Object.values(atual).some(i => i.contrato !== item.contrato) ? {} : { ...atual };
        base[item.id] = item;
        return base;
    };

    const itemDaParcela = (p, acordo) => ({
        id: p.id, contrato: false, acordoId: acordo.id, numero: p.numero, boletoAtivo: p.boletoAtivo,
        titulo: `Parcela ${p.numero}/${p.total} · acordo #${acordo.id}`, detalhe: `venc. ${dataBR(p.vencimento)}`,
        saldo: p.saldo, valorPago: valorEditavel(p.saldo), quitar: true,
    });

    const alternarParcela = (p, acordo) => {
        setSelecao(atual => {
            if (atual[p.id]) {
                const { [p.id]: _, ...resto } = atual;
                return resto;
            }
            return incluir(atual, itemDaParcela(p, acordo));
        });
    };

    const marcarAcordo = (acordo, marcar) => {
        setSelecao(atual => {
            let novo = { ...atual };
            acordo.parcelas.filter(p => p.emAberto).forEach(p => {
                if (marcar) novo = incluir(novo, itemDaParcela(p, acordo));
                else delete novo[p.id];
            });
            return novo;
        });
    };

    const alternarDebito = (d) => {
        setSelecao(atual => {
            if (atual[d.id]) {
                const { [d.id]: _, ...resto } = atual;
                return resto;
            }
            return incluir(atual, {
                id: d.id, contrato: true, boletoAtivo: false,
                titulo: `${d.titulo || "Débito"}${d.parcela ? ` · parcela ${d.parcela}` : ""}`, detalhe: `venc. ${dataBR(d.vencimento)}`,
                // Pago menos que o saldo: por padrão o restante fica em aberto (baixa parcial); o desconto é escolha
                saldo: d.saldo, valorPago: valorEditavel(d.saldo), quitar: false,
            });
        });
    };

    const mudarValor = (id, valor) => setSelecao(atual => (atual[id] ? { ...atual, [id]: { ...atual[id], valorPago: valor } } : atual));
    const mudarQuitar = (id, quitar) => setSelecao(atual => (atual[id] ? { ...atual, [id]: { ...atual[id], quitar } } : atual));
    const remover = (id) => setSelecao(({ [id]: _, ...resto }) => resto);

    // Parcela anterior às marcadas ainda em aberto no mesmo acordo: só um lembrete, não impede a baixa
    const avisos = useMemo(() => {
        if (!ficha || selecaoContrato) return [];
        const lista = [];
        (ficha.acordos || []).forEach(a => {
            const marcadas = a.parcelas.filter(p => selecao[p.id]);
            if (marcadas.length === 0) return;
            const menor = Math.min(...marcadas.map(p => p.numero || 0));
            const anteriores = a.parcelas.filter(p => p.emAberto && !selecao[p.id] && (p.numero || 0) < menor);
            if (anteriores.length > 0) {
                lista.push(`No acordo #${a.id}, ${anteriores.length === 1 ? `a parcela ${anteriores[0].numero}` : `${anteriores.length} parcelas`}, anterior${anteriores.length === 1 ? "" : "es"} às marcadas, continua${anteriores.length === 1 ? "" : "m"} em aberto.`);
            }
        });
        return lista;
    }, [ficha, selecao, selecaoContrato]);

    // ------------------------------------------------------------------------------------------- baixa e estorno
    const confirmarBaixa = ({ dataPagamento, formaPagamento, observacao, arquivo, acordoPagamento, negociador }) => {
        const contrato = selecaoContrato;
        // No acordo de pagamento não há desconto: o que falta continua em aberto
        const quitarDe = (i) => (acordoPagamento ? false : i.quitar);
        const dados = {
            idFicha: ficha.ficha.id,
            contrato,
            dataPagamento,
            formaPagamento,
            observacao: observacao?.trim() || null,
            acordoPagamento: Boolean(acordoPagamento),
            negociador: acordoPagamento ? negociador : null,
            itens: itensSelecionados.map(i => {
                const { desconto, acrescimo } = diferenca(i.saldo, i.valorPago, quitarDe(i));
                return { id: i.id, valorPago: numero(i.valorPago), valorDesconto: desconto, valorAcrescimo: acrescimo };
            }),
        };
        const emAberto = itensSelecionados.reduce((t, i) => t + diferenca(i.saldo, i.valorPago, quitarDe(i)).restante, 0);
        const formData = new FormData();
        formData.append("dados", JSON.stringify(dados));
        if (arquivo) formData.append("arquivos", arquivo);
        setEnviando(true);
        postUpload("/baixa-pagamento/baixar", formData)
            .then(res => {
                const partes = [res.acordoPagamento
                    ? `Acordo de pagamento nº ${res.acordoPagamento} registrado no nome de ${res.negociador} (${res.itens} débito(s))${res.comprovante ? ", com o comprovante na aba Documentos da ficha" : ""}.`
                    : `${res.itens} ${contrato ? "débito(s) baixado(s)" : "parcela(s) baixada(s)"}${res.comprovante ? ", com o comprovante na aba Documentos da ficha" : ""}.`];
                if (emAberto > 0) partes.push(`Baixa parcial: ${toCurrency(arredondar(emAberto))} continua em aberto.`);
                (res.acordosQuitados || []).forEach(a => partes.push(`${a} quitado.`));
                if ((res.boletosCancelados || []).length > 0) partes.push(`Boleto cancelado: ${res.boletosCancelados.join("; ")}.`);
                avisarFichaAlterada(); // a ficha aberta na Base de Dados (ou em outra aba) recarrega na hora
                abrirFicha(ficha.ficha.id, { tipo: "success", texto: partes.join(" ") });
            })
            .catch(err => setAviso({ tipo: "error", texto: err.message || "Erro ao baixar." }))
            .finally(() => setEnviando(false));
    };

    const estornar = (p) => {
        infoAviso(
            `Estornar a baixa de <b>${escaparHtml(p.item)}</b> (${toCurrency(p.valorPago)}, pago em ${dataBR(p.dataPagamento)})?<br/><br/>`
            + "O item volta a ficar em aberto e o estorno vai para o histórico da ficha.",
            () => {
                setCarregando(true);
                post("/baixa-pagamento/estornar", { id: p.idItem, contrato: p.contrato })
                    .then(() => {
                        avisarFichaAlterada();
                        abrirFicha(ficha.ficha.id, { tipo: "success", texto: `Baixa estornada: ${p.item}.` });
                    })
                    .catch(err => {
                        setAviso({ tipo: "error", texto: err.message || "Erro ao estornar." });
                        setCarregando(false);
                    });
            }
        );
    };

    // -------------------------------------------------------------------------------------------------- telas
    const cabecalhoTabela = (colunas) => (
        <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
                {colunas.map(c => (
                    <TableCell key={c} sx={{ py: 0.75, px: 1, backgroundColor: cores.painelEscuro }}>
                        <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ fontSize: FONTE.cabecalho }}>{c}</MDTypography>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );

    const resultados = resultado?.resultados || [];
    const parcelasEmAberto = ficha ? (ficha.acordos || []).reduce((t, a) => t + a.emAberto, 0) : 0;

    return (
        <MDBox pt={1} pb={3}>
            <Card sx={{ px: 2, py: 1.5, mb: 2 }}>
                <Grid container spacing={1.5} alignItems="flex-end">
                    <Grid item xs={12} md={9}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Buscar devedor</InputLabel>
                        <MDInput size="small" fullWidth value={busca} onChange={(e) => setBusca(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") pesquisar(); }} onDoubleClick={trazerFichaAberta}
                            placeholder="Nome, CPF/CNPJ, código do cliente, nº da ficha, nº do acordo, nosso número ou linha digitável" />
                    </Grid>
                    <Grid item xs={12} md={3} display="flex" gap={1}>
                        <MDButton variant="contained" color="info" size="medium" onClick={pesquisar} disabled={buscando} sx={{ flex: 1, height: 38 }}>
                            <Icon sx={{ mr: 0.5 }}>search</Icon>{buscando ? "Buscando..." : "Pesquisar"}
                        </MDButton>
                        {(resultado || ficha) && (
                            <MDButton variant="outlined" color="secondary" size="medium" onClick={novaBusca} sx={{ height: 38 }}>Limpar</MDButton>
                        )}
                    </Grid>
                </Grid>
                <MDTypography variant="caption" color="text" display="block" mt={0.75}>
                    Com o boleto ou o comprovante na mão, busque pelo nosso número ou pela linha digitável: a ficha certa já abre.
                    Dois cliques no campo trazem a ficha aberta na Base de Dados.
                </MDTypography>
            </Card>

            {aviso && (
                <Alert severity={aviso.tipo} onClose={() => setAviso(null)} sx={{ mb: 2, fontSize: "0.85rem" }}>{aviso.texto}</Alert>
            )}

            {!ficha && resultados.length > 0 && (
                <Card sx={{ p: 2, mb: 2 }}>
                    <MDTypography variant="caption" color="text" display="block" mb={1}>
                        {resultados.length} devedor(es) encontrado(s){resultado.maisResultados ? " (há mais: refine a busca)" : ""}. Escolha a ficha.
                    </MDTypography>
                    <TableContainer sx={{ border: `1px solid ${cores.borda}`, borderRadius: 2 }}>
                        <Table size="small" sx={{ minWidth: 720 }}>
                            {cabecalhoTabela(["Devedor", "Cliente", "Encontrado por", "Fichas"])}
                            <TableBody>
                                {resultados.map(r => (
                                    <TableRow key={r.idDevedor}>
                                        <TableCell sx={{ py: 1, px: 1, verticalAlign: "top" }}>
                                            <MDTypography variant="button" fontWeight="bold" display="block">{r.nome}</MDTypography>
                                            <MDTypography variant="caption" color="text" display="block">
                                                {identificacao(r.documento, r.codigoCliente, null)}
                                            </MDTypography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1, px: 1, verticalAlign: "top" }}><MDTypography variant="button">{r.cliente || "-"}</MDTypography></TableCell>
                                        <TableCell sx={{ py: 1, px: 1, verticalAlign: "top" }}><MDTypography variant="caption">{r.encontradoPor}</MDTypography></TableCell>
                                        <TableCell sx={{ py: 1, px: 1, verticalAlign: "top" }}>
                                            <MDBox display="flex" flexWrap="wrap" gap={0.75}>
                                                {r.fichas.map(f => (
                                                    <MDButton key={f.id} size="small" variant={f.id === r.idFichaSugerida ? "contained" : "outlined"} color="info"
                                                        onClick={() => abrirFicha(f.id)} sx={{ textTransform: "none" }}>
                                                        {rotuloFicha(f)}
                                                        {f.parcelasEmAberto > 0 ? ` · ${f.parcelasEmAberto} em aberto` : ""}
                                                        {f.parcelasAtrasadas > 0 ? ` (${f.parcelasAtrasadas} em atraso)` : ""}
                                                        {f.debitosAvulsos > 0 ? ` · ${f.debitosAvulsos} débito(s)` : ""}
                                                    </MDButton>
                                                ))}
                                            </MDBox>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}

            {carregando && <LinearProgress color="info" sx={{ mb: 1 }} />}

            {ficha && (
                <>
                    <Card sx={{ p: 2, mb: 2 }}>
                        <MDBox display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="flex-start" gap={1}>
                            <MDBox>
                                <MDTypography variant="h6">{ficha.nome}</MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                    {identificacao(ficha.documento, ficha.codigoCliente, ficha.ficha.numero)}{ficha.cliente ? ` · ${ficha.cliente}` : ""}
                                </MDTypography>
                            </MDBox>
                            {resultados.length > 1 && (
                                <MDButton variant="text" color="info" size="small" onClick={() => { setFicha(null); setSelecao({}); }}>
                                    <Icon sx={{ mr: 0.5 }}>arrow_back</Icon> Voltar aos {resultados.length} resultados
                                </MDButton>
                            )}
                        </MDBox>
                        <MDBox display="flex" flexWrap="wrap" gap={0.75} mt={1.5}>
                            {(ficha.fichasDoDevedor || []).map(f => {
                                const atual = f.id === ficha.ficha.id;
                                return (
                                    <Chip key={f.id} size="small" clickable={!atual} color={atual ? "info" : "default"} variant={atual ? "filled" : "outlined"}
                                        // No escuro, o azul do tema com texto branco some no cinza: a ficha aberta fica no azul claro com texto
                                        // escuro, e as outras com texto branco (o padrão do chip sem cor é texto preto)
                                        sx={!darkMode ? undefined : atual
                                            ? { backgroundColor: TEXTO_COLORIDO_ESCURO.info, color: CLARO.texto, fontWeight: "bold" }
                                            : { color: cores.texto, borderColor: cores.bordaForte }}
                                        onClick={() => !atual && abrirFicha(f.id)}
                                        label={`${rotuloFicha(f)}${f.parcelasEmAberto > 0 ? ` · ${f.parcelasEmAberto} em aberto` : ""}`} />
                                );
                            })}
                        </MDBox>
                        <MDBox display="flex" flexWrap="wrap" gap={3} mt={1.5}>
                            <MDBox>
                                <MDTypography variant="caption" color="text" display="block">Situação · status</MDTypography>
                                <MDTypography variant="button" fontWeight="medium">{ficha.ficha.situacao || "-"} · {ficha.ficha.status || "sem status"}</MDTypography>
                            </MDBox>
                            <MDBox>
                                <MDTypography variant="caption" color="text" display="block">Parcelas em aberto</MDTypography>
                                <MDTypography variant="button" fontWeight="medium">
                                    {parcelasEmAberto}{ficha.ficha.parcelasAtrasadas > 0 ? ` (${ficha.ficha.parcelasAtrasadas} em atraso)` : ""}
                                </MDTypography>
                            </MDBox>
                            <MDBox>
                                <MDTypography variant="caption" color="text" display="block">Débitos avulsos em aberto</MDTypography>
                                <MDTypography variant="button" fontWeight="medium">{(ficha.debitos || []).filter(d => !d.acordoAtivo).length}</MDTypography>
                            </MDBox>
                        </MDBox>
                    </Card>

                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={8}>
                            <Card sx={{ p: 2 }}>
                                <MDBox display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={1} mb={1.5}>
                                    <Tabs value={aba} onChange={(e, v) => setAba(v)} sx={{ minWidth: { md: 520 } }}>
                                        <Tab value="parcelas" label={`Parcelas de acordo (${parcelasEmAberto})`} />
                                        <Tab value="debitos" label={`Débitos avulsos (${(ficha.debitos || []).length})`} />
                                        <Tab value="historico" label={`Histórico (${(ficha.pagamentos || []).length})`} />
                                    </Tabs>
                                    {aba === "parcelas" && (
                                        <FormControlLabel control={<Switch checked={mostrarPagas} onChange={(e) => setMostrarPagas(e.target.checked)} />}
                                            label={<MDTypography variant="caption">Mostrar pagas</MDTypography>} />
                                    )}
                                </MDBox>
                                {aba === "parcelas" && (
                                    <TabelaParcelas acordos={ficha.acordos} mostrarPagas={mostrarPagas} selecionados={selecao}
                                        onAlternar={alternarParcela} onMarcarAcordo={marcarAcordo} />
                                )}
                                {aba === "debitos" && <TabelaDebitos debitos={ficha.debitos} selecionados={selecao} onAlternar={alternarDebito} />}
                                {aba === "historico" && (
                                    <HistoricoPagamentos pagamentos={ficha.pagamentos} podeEstornar={ficha.podeEstornar} onEstornar={estornar} />
                                )}
                            </Card>
                        </Grid>
                        <Grid item xs={12} lg={4}>
                            <PainelPagamento key={painelKey} itens={itensSelecionados} contrato={selecaoContrato} formas={ficha.formas}
                                negociadores={ficha.negociadores} negociadorPadrao={ficha.negociadorPadrao} semNegociadores={ficha.semNegociadores}
                                avisos={avisos} enviando={enviando} onValor={mudarValor} onQuitar={mudarQuitar} onRemover={remover}
                                onLimpar={() => setSelecao({})} onConfirmar={confirmarBaixa} />
                        </Grid>
                    </Grid>
                </>
            )}
        </MDBox>
    );
}
